import React, { useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Reveal } from '../Reveal';
import { tomos } from '../../data/content';
import type { Tomo } from '../../types/content';
const estilos = [{
  borde: 'border-l-blue-500',
  icono: 'bg-blue-50',
  etiqueta: 'bg-blue-50 text-blue-700',
  panel: 'bg-blue-50/70'
}, {
  borde: 'border-l-verde',
  icono: 'bg-verde/15',
  etiqueta: 'bg-verde/15 text-verde-dark',
  panel: 'bg-verde/5'
}, {
  borde: 'border-l-amber-400',
  icono: 'bg-amber-50',
  etiqueta: 'bg-amber-50 text-amber-700',
  panel: 'bg-amber-50/70'
}, {
  borde: 'border-l-red-500',
  icono: 'bg-red-50',
  etiqueta: 'bg-red-50 text-red-700',
  panel: 'bg-red-50/70'
}, {
  borde: 'border-l-violet-500',
  icono: 'bg-violet-50',
  etiqueta: 'bg-violet-50 text-violet-700',
  panel: 'bg-violet-50/70'
}, {
  borde: 'border-l-pink-500',
  icono: 'bg-pink-50',
  etiqueta: 'bg-pink-50 text-pink-700',
  panel: 'bg-pink-50/70'
}, {
  borde: 'border-l-bordo',
  icono: 'bg-bordo/10',
  etiqueta: 'bg-bordo/10 text-bordo',
  panel: 'bg-bordo/[0.04]'
}];
interface TomoRowProps {
  tomo: Tomo;
  index: number;
  open: boolean;
  onToggle: () => void;
}
function TomoRow({
  tomo,
  index,
  open,
  onToggle
}: TomoRowProps) {
  const estilo = estilos[index];
  const panelId = `tomo-panel-${index}`;
  const triggerId = `tomo-trigger-${index}`;
  return <article data-tomo-index={index} className={`overflow-hidden rounded-2xl border border-black/[0.07] border-l-4 ${estilo.borde} bg-white shadow-[0_7px_24px_rgba(17,24,39,0.07)] transition-shadow duration-300 hover:shadow-[0_12px_30px_rgba(17,24,39,0.11)]`} data-id="element-269" data-mp="3em1m0bkrdw09">
      <h3 data-id="element-270" data-mp="679ul54a37jx">
        <button id={triggerId} type="button" onClick={onToggle} aria-expanded={open} aria-controls={panelId} className="flex w-full items-start gap-3 px-4 py-5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-bordo sm:gap-5 sm:px-7 sm:py-6" data-id="element-271" data-mp="671i3ec2i499">
          <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-xl ${estilo.icono} sm:h-12 sm:w-12 sm:text-2xl`} aria-hidden="true" data-id="element-272" data-mp="6y9n666lsh10">
            {tomo.icono}
          </span>

          <span className="min-w-0 flex-1" data-id="element-273" data-mp="gukxwrqjoytc">
            <span className="flex flex-col items-start gap-2 lg:flex-row lg:items-center lg:gap-3" data-id="element-274" data-mp="gy0iga2zn025">
              <span className="font-display text-lg font-bold leading-snug text-tinta sm:text-2xl" data-id="element-275" data-mp="kuwkia2gx97g">
                {tomo.numero} — {tomo.titulo}
              </span>
              <span className={`shrink-0 rounded-md px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide sm:text-xs ${estilo.etiqueta}`} data-id="element-276" data-mp="utufnvueg0zf">
                {tomo.etiqueta}
              </span>
            </span>
            <span className="mt-2 block text-sm font-medium leading-relaxed text-tintasuave sm:text-base" data-id="element-277" data-mp="14nb9gzujdg33">
              {tomo.descripcion}
            </span>
          </span>

          <ChevronDown className={`mt-1 h-5 w-5 shrink-0 text-tintasuave transition-transform duration-300 ${open ? 'rotate-180 text-bordo' : ''}`} aria-hidden="true" data-id="element-278" data-mp="1dmoixsinu4nt" />
        </button>
      </h3>

      <div id={panelId} role="region" aria-labelledby={triggerId} className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-279" data-mp="2t77dazyt4opj">
        <div className="overflow-hidden" data-id="element-280" data-mp="3gtdwmidolqv2">
          <div className={`border-t border-black/[0.05] px-5 py-5 sm:px-[6.75rem] sm:py-6 ${estilo.panel}`} data-id="element-281" data-mp="3gw9te3dsm9hx">
            <p className="text-sm font-medium leading-relaxed text-tinta sm:text-lg" data-id="element-282" data-mp="382559rq1nc63">{tomo.descripcionCompleta}</p>
          </div>
        </div>
      </div>
    </article>;
}
export function TomosCarousel() {
  const [openIndex, setOpenIndex] = useState(0);
  const touchStartY = useRef<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const selectTomo = (index: number) => {
    const nextIndex = Math.max(0, Math.min(tomos.length - 1, index));
    setOpenIndex(nextIndex);
    window.setTimeout(() => {
      const selected = listRef.current?.querySelector<HTMLElement>(`[data-tomo-index="${nextIndex}"]`);
      selected?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 40);
  };
  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartY.current === null) return;
    const distance = event.changedTouches[0].clientY - touchStartY.current;
    touchStartY.current = null;
    if (Math.abs(distance) < 50) return;
    selectTomo(openIndex + (distance < 0 ? 1 : -1));
  };
  return <section aria-labelledby="tomos-titulo" className="w-full bg-grisclaro px-4 py-16 sm:px-6 sm:py-20" data-id="element-283" data-mp="3p9uyini2ofso">
      <div className="mx-auto max-w-5xl" data-id="element-284" data-mp="3u7msro66we01">
        <Reveal data-id="element-285" data-mp="ltxnkcc373ta">
          <div className="text-center" data-id="element-286" data-mp="lte3aoyys3c3">
            <span className="inline-flex rounded-full bg-bordo/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-bordo" data-id="element-287" data-mp="lscqog2av8s9">
              Biblioteca profesional completa
            </span>
            <h2 id="tomos-titulo" className="mt-5 font-display text-3xl font-bold text-bordo sm:text-5xl" data-id="element-288" data-mp="zxd248kpflg3">
              Los 7 Tomos
            </h2>
            <p className="mt-3 text-base text-tintasuave sm:text-lg" data-id="element-289" data-mp="19p93ptm01tp2">
              Para profes de telas de todos los niveles y enfoques
            </p>
          </div>
        </Reveal>

        <div className="mt-10 flex items-center justify-center gap-3 sm:mt-12" data-id="element-290" data-mp="h0240rat6y28">
          <div ref={listRef} className="w-full space-y-4" onTouchStart={event => {
          touchStartY.current = event.touches[0].clientY;
        }} onTouchEnd={handleTouchEnd} data-id="element-291" data-mp="h253e1xuq3jm">
            {tomos.map((tomo, index) => <Reveal key={tomo.numero} delay={Math.min(index, 3) * 0.04} data-id="element-292" data-mp="lzevjpcmktb5" data-mpi={index}>
                <TomoRow tomo={tomo} index={index} open={openIndex === index} onToggle={() => selectTomo(index)} data-id="element-293" data-mp="m1hsyug3xopw" />
              </Reveal>)}
          </div>

          <div className="sticky top-1/2 hidden shrink-0 flex-col gap-2 self-start pt-24 sm:flex" aria-label="Navegación de tomos" data-id="element-294" data-mp="1xdsdkdkp6ej9">
            <button type="button" onClick={() => selectTomo(openIndex - 1)} disabled={openIndex === 0} aria-label="Tomo anterior" className="flex h-11 w-11 items-center justify-center rounded-full border border-black/10 bg-white text-bordo shadow-soft transition-colors hover:bg-bordo hover:text-white disabled:cursor-not-allowed disabled:opacity-35" data-id="element-295" data-mp="25hci6eo14r8k">
              <ChevronUp className="h-5 w-5" aria-hidden="true" data-id="element-296" data-mp="1lsbs2n5f2bfm" />
            </button>
            <button type="button" onClick={() => selectTomo(openIndex + 1)} disabled={openIndex === tomos.length - 1} aria-label="Tomo siguiente" className="flex h-11 w-11 items-center justify-center rounded-full border border-black/10 bg-white text-bordo shadow-soft transition-colors hover:bg-bordo hover:text-white disabled:cursor-not-allowed disabled:opacity-35" data-id="element-297" data-mp="1qptzg93scili">
              <ChevronDown className="h-5 w-5" aria-hidden="true" data-id="element-298" data-mp="12yybxo6d49k2" />
            </button>
          </div>
        </div>
      </div>
    </section>;
}